/* SIWS CUSTOM: Pocket Screwdriver Color Picker — screwdriver-picker.js
   Handles color selection, 150-unit minimum enforcement (50 per color),
   dynamic price display, and Ajax cart submission to the Multicolor variant.
   Initialized on DOMContentLoaded for all .screwdriver-picker elements. */

'use strict';

// SIWS CUSTOM: Orderable colors for Pocket Screwdrivers — 12 colors total.
// hex values are for swatch display only; actual product color is determined by variant/order note.
const SIWS_SCREWDRIVER_COLORS = [
  { id: 'red',              name: 'Red',              hex: '#D32F2F' },
  { id: 'orange',           name: 'Orange',           hex: '#E65100' },
  { id: 'green',            name: 'Green',            hex: '#2E7D32' },
  { id: 'black',            name: 'Black',            hex: '#212121' },
  { id: 'transparent-blue', name: 'Transparent Blue', hex: '#90CAF9' },
  { id: 'medium-blue',      name: 'Medium Blue',      hex: '#1565C0' },
  { id: 'neon-green',       name: 'Neon Green',       hex: '#76FF03' },
  { id: 'neon-pink',        name: 'Neon Pink',        hex: '#FF4081' },
  { id: 'neon-yellow',      name: 'Neon Yellow',      hex: '#FFD600' },
  { id: 'pearl-yellow',     name: 'Pearl Yellow',     hex: '#F9C440' },
  { id: 'pearl-green',      name: 'Pearl Green',      hex: '#69B47B' },
];

// SIWS CUSTOM: Order constraints — enforced on ATC, shown in UI as warnings.
const SIWS_SCREWDRIVER_MIN_TOTAL     = 150;  // minimum total units per order
const SIWS_SCREWDRIVER_MIN_PER_COLOR = 50;   // minimum units per color if selected (qty > 0)
const SIWS_SCREWDRIVER_UNIT_PRICE    = 2.10; // dollars per unit (must match Shopify admin pricing)

// SIWS CUSTOM: SIWSScrewdriverPicker — manages all color picker state and interactions.
class SIWSScrewdriverPicker {
  constructor(el) {
    this.el            = el;
    this.variantId     = el.dataset.variantId;
    this.productTitle  = el.dataset.productTitle  || 'Pocket Screwdrivers';
    this.customerName  = el.dataset.customerName  || '';
    this.customerEmail = el.dataset.customerEmail || '';

    this.colorQtys = {}; // colorId → integer qty

    this.init();
  }

  init() {
    this._renderColorCards();
    this._bindEvents();
    this._update();
  }

  // ── Queries ──────────────────────────────────────────────────

  _totalQty() {
    return Object.values(this.colorQtys).reduce((s, q) => s + q, 0);
  }

  // Colors with at least 1 unit selected, sorted by catalog order.
  _selectedColors() {
    return SIWS_SCREWDRIVER_COLORS.filter(c => (this.colorQtys[c.id] || 0) > 0);
  }

  _dealerName() {
    const el = this.el.querySelector('#screwdriver-dealer-name');
    return el ? el.value.trim() : '';
  }

  _phone() {
    const el = this.el.querySelector('#screwdriver-phone');
    return el ? el.value.trim() : '';
  }

  _isValid() {
    if (!this._dealerName() || !this._phone()) return false;
    if (this._totalQty() < SIWS_SCREWDRIVER_MIN_TOTAL) return false;
    return this._selectedColors().every(c =>
      (this.colorQtys[c.id] || 0) >= SIWS_SCREWDRIVER_MIN_PER_COLOR
    );
  }

  // Returns the user-facing reason the ATC button is blocked, or '' if valid.
  _validationMessage() {
    if (!this._dealerName()) return 'Dealer name is required';
    if (!this._phone())      return 'Phone number is required';
    // Per-color minimum takes priority — more actionable than the total message.
    const underMin = this._selectedColors().filter(c =>
      (this.colorQtys[c.id] || 0) < SIWS_SCREWDRIVER_MIN_PER_COLOR
    );
    if (underMin.length) {
      const names = underMin.map(c => c.name).join(', ');
      return `${SIWS_SCREWDRIVER_MIN_PER_COLOR} unit minimum per color: ${names}`;
    }
    const total = this._totalQty();
    if (total < SIWS_SCREWDRIVER_MIN_TOTAL) {
      if (total === 0) return `Minimum ${SIWS_SCREWDRIVER_MIN_TOTAL} units required`;
      return `${SIWS_SCREWDRIVER_MIN_TOTAL - total} more units needed (${total} of ${SIWS_SCREWDRIVER_MIN_TOTAL})`;
    }
    return '';
  }

  // ── Qty mutations ────────────────────────────────────────────

  _increment(id) {
    this.colorQtys[id] = (this.colorQtys[id] || 0) + 50;
    this._update();
  }

  _decrement(id) {
    const current = this.colorQtys[id] || 0;
    if (current <= 0) return;
    this.colorQtys[id] = current - 50;
    this._update();
  }

  // ── Card rendering ───────────────────────────────────────────

  _renderColorCards() {
    const container = this.el.querySelector('#screwdriver-color-cards');
    if (!container) {
      console.error('[SIWS] _renderColorCards: #screwdriver-color-cards not found');
      return;
    }

    const grid = document.createElement('div');
    grid.className = 'screwdriver-color-grid';

    SIWS_SCREWDRIVER_COLORS.forEach(color => {
      grid.appendChild(this._buildCardEl(color));
    });

    container.appendChild(grid);
    console.log('[SIWS] Screwdriver color cards rendered:', SIWS_SCREWDRIVER_COLORS.length);
  }

  _buildCardEl(color) {
    // SIWS CUSTOM: Card is a div — interactive elements are the stepper buttons inside.
    const card = document.createElement('div');
    card.className       = 'screwdriver-color-card';
    card.dataset.colorId = color.id;

    // Swatch circle
    const swatchWrap = document.createElement('div');
    swatchWrap.className = 'screwdriver-color-card__swatch-wrap';

    const swatch = document.createElement('span');
    swatch.className           = 'screwdriver-color-card__swatch';
    swatch.style.backgroundColor = color.hex;
    // White needs a border to be visible against light backgrounds.
    if (color.id === 'white') swatch.style.border = '2px solid rgba(0,0,0,0.15)';
    swatch.setAttribute('aria-hidden', 'true');
    swatchWrap.appendChild(swatch);

    // Color name
    const name = document.createElement('span');
    name.className   = 'screwdriver-color-card__name';
    name.textContent = color.name;

    // Stepper: [−] qty [+]
    const stepper = document.createElement('div');
    stepper.className = 'screwdriver-color-card__stepper';

    const minusBtn = document.createElement('button');
    minusBtn.type      = 'button';
    minusBtn.className = 'screwdriver-color-card__minus';
    minusBtn.dataset.colorId = color.id;
    minusBtn.textContent     = '−';
    minusBtn.setAttribute('aria-label', `Remove one ${color.name}`);
    minusBtn.disabled = true; // starts at 0

    const qtySpan = document.createElement('span');
    qtySpan.className   = 'screwdriver-color-card__qty';
    qtySpan.textContent = '0';

    const plusBtn = document.createElement('button');
    plusBtn.type      = 'button';
    plusBtn.className = 'screwdriver-color-card__plus';
    plusBtn.dataset.colorId = color.id;
    plusBtn.textContent     = '+';
    plusBtn.setAttribute('aria-label', `Add one ${color.name}`);

    stepper.appendChild(minusBtn);
    stepper.appendChild(qtySpan);
    stepper.appendChild(plusBtn);

    card.appendChild(swatchWrap);
    card.appendChild(name);
    card.appendChild(stepper);

    return card;
  }

  // ── Event binding ────────────────────────────────────────────

  _bindEvents() {
    // Stepper buttons — delegated on .screwdriver-picker.
    this.el.addEventListener('click', e => {
      const plusBtn  = e.target.closest('.screwdriver-color-card__plus');
      const minusBtn = e.target.closest('.screwdriver-color-card__minus');
      if (plusBtn)  this._increment(plusBtn.dataset.colorId);
      if (minusBtn) this._decrement(minusBtn.dataset.colorId);
    });

    // Dealer contact fields — re-validate on every keystroke.
    const nameEl  = this.el.querySelector('#screwdriver-dealer-name');
    const phoneEl = this.el.querySelector('#screwdriver-phone');
    if (nameEl)  nameEl.addEventListener('input', () => this._updateButton());
    if (phoneEl) phoneEl.addEventListener('input', () => this._updateButton());

    // Add to Cart.
    const atcBtn = this.el.querySelector('#screwdriver-atc-btn');
    if (atcBtn) atcBtn.addEventListener('click', () => this._addToCart());
  }

  // ── Coordinated update ───────────────────────────────────────

  _update() {
    this._updateCounter();
    this._updateCardStates();
    this._updateSummary();
    this._updateButton();
  }

  _updateCounter() {
    const total     = this._totalQty();
    const totalCost = (total * SIWS_SCREWDRIVER_UNIT_PRICE).toFixed(2);
    const pct       = Math.min(100, (total / SIWS_SCREWDRIVER_MIN_TOTAL) * 100);
    const met       = total >= SIWS_SCREWDRIVER_MIN_TOTAL;

    const countText = this.el.querySelector('#screwdriver-count-text');
    const priceText = this.el.querySelector('#screwdriver-price-text');
    const fillEl    = this.el.querySelector('#screwdriver-progress-fill');
    const counter   = this.el.querySelector('#screwdriver-counter');

    if (countText) {
      countText.textContent = met
        ? `${total} units — minimum met ✓`
        : `${total} of ${SIWS_SCREWDRIVER_MIN_TOTAL} minimum units`;
    }
    if (priceText) {
      priceText.textContent = total > 0 ? ` · $${totalCost}` : '';
    }
    if (fillEl) {
      fillEl.style.width = pct + '%';
      fillEl.classList.toggle('screwdriver-progress__fill--complete', met);
    }
    if (counter) {
      counter.classList.toggle('screwdriver-counter--complete', met);
    }
  }

  _updateCardStates() {
    this.el.querySelectorAll('.screwdriver-color-card').forEach(card => {
      const id  = card.dataset.colorId;
      const qty = this.colorQtys[id] || 0;

      // Selected (≥50): button-color border.
      // Warning (1–49): orange border — minimum not yet met for this color.
      card.classList.toggle('screwdriver-color-card--selected', qty >= SIWS_SCREWDRIVER_MIN_PER_COLOR);
      card.classList.toggle('screwdriver-color-card--warning',  qty > 0 && qty < SIWS_SCREWDRIVER_MIN_PER_COLOR);

      const plusBtn  = card.querySelector('.screwdriver-color-card__plus');
      const minusBtn = card.querySelector('.screwdriver-color-card__minus');
      const qtySpan  = card.querySelector('.screwdriver-color-card__qty');

      if (qtySpan)  qtySpan.textContent = qty;
      if (minusBtn) minusBtn.disabled   = qty === 0;
      // No upper cap per color — dealers choose their own mix.
      if (plusBtn)  plusBtn.disabled    = false;
    });
  }

  _updateSummary() {
    const summaryEl = this.el.querySelector('#screwdriver-summary');
    if (!summaryEl) return;

    const selected = this._selectedColors();
    if (selected.length === 0) {
      summaryEl.hidden = true;
      return;
    }

    const total     = this._totalQty();
    const totalCost = (total * SIWS_SCREWDRIVER_UNIT_PRICE).toFixed(2);

    const rows = selected.map(c => {
      const qty      = this.colorQtys[c.id];
      const lineCost = (qty * SIWS_SCREWDRIVER_UNIT_PRICE).toFixed(2);
      const warn     = qty < SIWS_SCREWDRIVER_MIN_PER_COLOR
        ? ' <span class="screwdriver-summary__warn">⚠ need 50</span>'
        : '';
      const border   = c.id === 'white' ? 'border:1px solid #ccc;' : '';
      return `<tr>
        <td>
          <span class="screwdriver-summary__dot" style="background:${c.hex};${border}"></span>
          ${c.name}${warn}
        </td>
        <td class="screwdriver-summary__qty-cell">${qty} × $${SIWS_SCREWDRIVER_UNIT_PRICE.toFixed(2)} = $${lineCost}</td>
      </tr>`;
    }).join('');

    summaryEl.innerHTML = `
      <h4 class="screwdriver-summary__title">Adding to Cart</h4>
      <table class="screwdriver-summary__table">
        <tbody>${rows}</tbody>
        <tfoot>
          <tr class="screwdriver-summary__total-row">
            <td>Total</td>
            <td class="screwdriver-summary__qty-cell">${total} units · $${totalCost}</td>
          </tr>
        </tfoot>
      </table>
    `;
    summaryEl.hidden = false;
  }

  _updateButton() {
    const atcBtn = this.el.querySelector('#screwdriver-atc-btn');
    if (!atcBtn) return;

    const valid   = this._isValid();
    const total   = this._totalQty();
    const cost    = (total * SIWS_SCREWDRIVER_UNIT_PRICE).toFixed(2);

    atcBtn.disabled    = !valid;
    atcBtn.textContent = valid
      ? `Add ${total} Units to Cart — $${cost}`
      : (this._validationMessage() || `Minimum ${SIWS_SCREWDRIVER_MIN_TOTAL} units required`);
  }

  // ── Shared helpers ───────────────────────────────────────────

  _cartRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root)
      ? window.Shopify.routes.root
      : '/';
  }

  _hideError() {
    const el = this.el.querySelector('#screwdriver-atc-error');
    if (el) el.hidden = true;
  }

  _showError(message) {
    const el = this.el.querySelector('#screwdriver-atc-error');
    if (!el) return;
    el.textContent = message;
    el.hidden      = false;
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  // ── Add to Cart ──────────────────────────────────────────────

  async _addToCart() {
    const atcBtn = this.el.querySelector('#screwdriver-atc-btn');
    if (!atcBtn || atcBtn.disabled) return;

    const terms = 'Invoice to Follow';

    const selected = this._selectedColors();
    const total    = this._totalQty();

    // SIWS CUSTOM: Build line item properties.
    // One property per selected color so M&C can read the breakdown at a glance in Shopify admin.
    // _color_breakdown (hidden) is a compact summary string for downstream use.
    const properties = {
      'Dealer Name':   this._dealerName(),
      'Phone Number':  this._phone(),
      'Payment terms': terms,
      'Total units':   String(total),
    };

    selected.forEach(c => {
      properties[c.name] = String(this.colorQtys[c.id]);
    });

    properties['_color_breakdown'] = selected
      .map(c => `${c.name}: ${this.colorQtys[c.id]}`)
      .join(', ');

    const originalText = atcBtn.textContent;
    atcBtn.disabled    = true;
    atcBtn.textContent = 'Adding to cart…';
    this._hideError();

    const postJson = (url, body) => fetch(url, {
      method:  'POST',
      headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body:    JSON.stringify(body),
    });

    try {
      // Single line item: the Multicolor variant, quantity = total units ordered.
      const cartRes = await postJson(`${this._cartRoot()}cart/add.js`, {
        items: [{ id: this.variantId, quantity: total, properties }],
      });

      if (!cartRes.ok) {
        const err = await cartRes.json().catch(() => ({}));
        throw new Error(err.description || 'Could not add items to cart.');
      }

      window.location.href = `${this._cartRoot()}cart`;

    } catch (err) {
      console.error('[SIWS] Screwdriver add-to-cart error:', err);
      atcBtn.disabled    = false;
      atcBtn.textContent = originalText;
      this._showError(err.message || 'Something went wrong. Please try again.');
    }
  }
}

// SIWS CUSTOM: Initialize SIWSScrewdriverPicker for every .screwdriver-picker on the page.
// readyState guard handles deferred-script timing edge cases.
function _siwsInitScrewdriverPickers() {
  document.querySelectorAll('.screwdriver-picker').forEach(el => {
    new SIWSScrewdriverPicker(el);
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', _siwsInitScrewdriverPickers);
} else {
  _siwsInitScrewdriverPickers();
}
